import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { getLoadTrackingPosition, type LoadTrackingPosition } from "./api";

export function LoadTrackingPanel({
  companyId,
  loadId,
  posterView = false,
}: {
  companyId: string;
  loadId: string;
  posterView?: boolean;
}) {
  const [position, setPosition] = useState<LoadTrackingPosition | null>(),
    [error, setError] = useState(""),
    [now, setNow] = useState(Date.now());
  const node = useRef<HTMLDivElement>(null),
    map = useRef<L.Map | null>(null),
    marker = useRef<L.CircleMarker | null>(null);
  useEffect(() => {
    let alive = true;
    const refresh = () =>
      void getLoadTrackingPosition(companyId, loadId)
        .then((value) => {
          if (alive) {
            setPosition(value);
            setError("");
          }
        })
        .catch(() => {
          if (alive) setError("Live position is temporarily unavailable.");
        });
    refresh();
    const poll = window.setInterval(refresh, 25000),
      clock = window.setInterval(() => setNow(Date.now()), 10000);
    return () => {
      alive = false;
      window.clearInterval(poll);
      window.clearInterval(clock);
    };
  }, [companyId, loadId]);
  useEffect(() => {
    if (!node.current || !position) return;
    const point: L.LatLngExpression = [position.latitude, position.longitude];
    if (!map.current) {
      map.current = L.map(node.current).setView(point, 13);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap contributors</a>',
      }).addTo(map.current);
      marker.current = L.circleMarker(point, {
        radius: 9,
        color: "#07202d",
        weight: 3,
        fillColor: "#45c9f0",
        fillOpacity: 1,
      }).addTo(map.current);
    } else {
      marker.current?.setLatLng(point);
      map.current.panTo(point, { animate: true });
    }
  }, [position?.latitude, position?.longitude]);
  useEffect(
    () => () => {
      map.current?.remove();
      map.current = null;
    },
    [],
  );
  if (position === undefined)
    return (
      <section className="load-tracking-panel">
        <strong>Live vehicle position</strong>
        <small>Checking for a tracking update…</small>
      </section>
    );
  if (!position)
    return (
      <section className="load-tracking-panel">
        <strong>Live vehicle position</strong>
        <small>
          {error ||
            (posterView
              ? "No shared position is currently available. The carrier may not have started tracking or may have disabled sharing."
              : "No position has been received yet. Tracking will appear here automatically once the assigned vehicle reports.")}
        </small>
      </section>
    );
  const age = Math.max(
      0,
      Math.floor((now - new Date(position.recordedAt).getTime()) / 1000),
    ),
    fresh = age < 90,
    source =
      position.source === "loadsub_device"
        ? "Driver device GPS"
        : position.source
            .replaceAll("_", " ")
            .replace(/\b\w/g, (x) => x.toUpperCase());
  return (
    <section className="load-tracking-panel">
      <div className="load-tracking-heading">
        <div>
          <strong>Live vehicle position · {position.registration}</strong>
          <small>
            {source} ·{" "}
            {age < 60 ? `${age}s ago` : `${Math.floor(age / 60)}m ago`}
            {position.speedKph !== null
              ? ` · ${Math.round(position.speedKph)} km/h`
              : ""}
          </small>
        </div>
        <span
          className={`status-chip ${fresh ? "status-active" : "status-grace"}`}
        >
          {fresh ? "Live" : "Delayed"}
        </span>
      </div>
      <div
        ref={node}
        className="load-tracking-map"
        aria-label={`Map showing ${position.registration}'s latest position`}
      />
      <small className="load-tracking-note">
        Position map uses OpenStreetMap and requires no additional map
        subscription. Route and ETA calculations are separate.
      </small>
    </section>
  );
}
